import { useId, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSession } from '../state/session.tsx';
import { EmptyState, PageHeader } from './ui.tsx';
import { Icon, type IconName } from './icons.tsx';
import './work-page.css';

/** A shared hierarchy for working pages, without dictating their content layout. */
export function WorkPage({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="work-page">
      <PageHeader title={title} description={description} actions={actions} />
      {children}
    </div>
  );
}

export function WorkSection({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section className="card work-section" aria-labelledby={id}>
      <div className="work-section-heading">
        <div>
          <h2 id={id}>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export function WorkEmpty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <EmptyState title={title} className="work-empty">
      {children}
    </EmptyState>
  );
}

export type StaffDestination = 'updates' | 'documents' | 'preferences' | 'time-off' | 'swaps' | 'manage' | 'photos';

/** Plain links preserve existing page/history and draft lifecycle semantics. */
export function StaffNavigation({
  active,
  canManage = false,
  ownDocuments = false,
  clientNavigation = false,
  swaps = false,
}: {
  active: StaffDestination;
  canManage?: boolean;
  ownDocuments?: boolean;
  clientNavigation?: boolean;
  /** `swaps` on `GET /staff/capability`: whether the Swaps entry shows beside Time off. */
  swaps?: boolean;
}) {
  const { i18n } = useTranslation();
  const canManageUsers = Boolean(useSession().user?.permissions?.includes('users:manage'));
  const es = i18n.language.startsWith('es');
  const links: { key: string; href: string; label: string; icon: IconName }[] = [
    { key: 'updates', href: '/staff', label: es ? 'Novedades' : 'Updates', icon: 'message' },
    {
      key: 'documents',
      href: '/staff/documents',
      label: ownDocuments
        ? es
          ? 'Mis documentos'
          : 'My documents'
        : es
          ? 'Documentos del personal'
          : 'Staff documents',
      icon: 'quote',
    },
    {
      key: 'preferences',
      href: '/staff/preferences',
      label: es ? 'Ajustes de notificaciones' : 'Notification settings',
      icon: 'bell',
    },
    // Time off and Swaps sit side by side: both are about who works when.
    ...(active === 'time-off' || active === 'swaps'
      ? [
          {
            key: 'time-off',
            href: '/staff/time-off',
            label: es ? 'Tiempo libre' : 'Time off',
            icon: 'calendar' as const,
          },
          ...(swaps || active === 'swaps'
            ? [
                {
                  key: 'swaps',
                  href: '/staff/swaps',
                  label: es ? 'Cambios de turno' : 'Swaps',
                  icon: 'users' as const,
                },
              ]
            : []),
        ]
      : []),
    ...(active === 'manage'
      ? [
          {
            key: 'manage',
            href: '/staff/manage',
            label: es ? 'Anuncios y acceso' : 'Announcements and access',
            icon: 'message' as const,
          },
        ]
      : []),
    // Managers run the staff portal, but the Team section in Settings renders
    // for `users:manage` (owners) only: the link would land on nothing.
    ...(canManage && canManageUsers
      ? [
          {
            key: 'team',
            href: '/settings#settings-team',
            label: es ? 'Personas y equipos' : 'People and teams',
            icon: 'users' as const,
          },
        ]
      : []),
  ];
  return (
    <nav className="card staff-section-nav" aria-label={es ? 'Recursos del personal' : 'Staff resources'}>
      {links.map((link) => {
        const content = (
          <>
            <span className="staff-nav-icon" aria-hidden="true">
              <Icon name={link.icon} size={18} />
            </span>
            <span>{link.label}</span>
          </>
        );
        const current = active === link.key;
        const onClick = current ? (event: MouseEvent) => event.preventDefault() : undefined;
        return clientNavigation ? (
          <Link key={link.key} to={link.href} aria-current={current ? 'page' : undefined} onClick={onClick}>
            {content}
          </Link>
        ) : (
          <a key={link.key} href={link.href} aria-current={current ? 'page' : undefined} onClick={onClick}>
            {content}
          </a>
        );
      })}
    </nav>
  );
}
